import { useEffect, useRef, useState } from 'react'
import { type ApexOptions } from 'apexcharts'
import classNames from 'classnames'
import merge from 'deepmerge'
import Chart from 'react-apexcharts'

import CustomChartLegend from 'components/CustomChartLegend'
import { COLORS } from 'consts'
import { humanizeLargeNumber } from 'utils'
import { getOptionsBasedCustomLegend } from 'utils/chartOptions'

import './index.scss'

const getDefaultOptions = (): ApexOptions => ({
  chart: {
    toolbar: {
      show: true,
      tools: {
        download: false,
        pan: false,
        reset: false,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
      },
    },
    animations: {
      enabled: false,
    },
    events: {
      mounted: chart => {
        chart.windowResizeHandler()
      },
    },
  },
  states: {
    hover: {
      filter: {
        type: 'none',
      },
    },
  },
  colors: [COLORS.primary, COLORS.graph_item_3],
  legend: {
    show: true,
    position: 'left',
    horizontalAlign: 'center',
    onItemHover: {
      highlightDataSeries: false,
    },
    itemMargin: {
      horizontal: 5,
    },
    markers: {
      size: 6,
      shape: 'circle',
    },
  },
  stroke: {
    show: false,
    width: 0,
  },
  tooltip: {
    enabled: true,
    y: {
      formatter: function (val, opts) {
        const percent = opts.globals.seriesPercent
        const currentIndex = opts.dataPointIndex
        const currentPercent = percent[currentIndex]
        return `${humanizeLargeNumber(currentPercent[0], 1, false, true)}% (${humanizeLargeNumber(val, 2)})`
      },
    },
  },
})

const ITEM_WIDTH = 100

interface IPieChart {
  height?: number | string
  width?: number | string
  options: Record<string, any>
  series: any[]
  labels: string[]
  title?: string
  id: string
  enableCustomLegends?: boolean
  className?: string
}

const PieChart = ({
  height = 'auto',
  width = '100%',
  options,
  series,
  id,
  labels,
  className,
  title: _title,
  enableCustomLegends = false,
  ...props
}: IPieChart) => {
  const pieChartContainerRef = useRef<HTMLDivElement>(null)
  const [legendLimit, setLegendLimit] = useState<number>(5)

  useEffect(() => {
    if (!pieChartContainerRef.current) return

    let animationFrameId: number

    const updateLimit = () => {
      animationFrameId = window.requestAnimationFrame(() => {
        const parentWidth = pieChartContainerRef.current?.offsetWidth || 0
        const maxItems = Math.floor(parentWidth / ITEM_WIDTH)
        setLegendLimit(maxItems > 0 ? maxItems : 1)
      })
    }

    updateLimit()

    const resizeObserver = new window.ResizeObserver(updateLimit)
    resizeObserver.observe(pieChartContainerRef.current)

    return () => {
      resizeObserver.disconnect()
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [width])

  const shouldEnableCustomLegends = enableCustomLegends

  const defaultOptions = getDefaultOptions()
  const mergedOptions = merge.all([defaultOptions, options, { labels, chart: { id } }], {
    arrayMerge: (destination, source) => source,
  }) as any
  const newOptions = getOptionsBasedCustomLegend({
    options: mergedOptions,
    enableCustomLegends: shouldEnableCustomLegends,
  })

  const seriesWithLabel = series?.map((item, index) => [labels[index], item]) as any
  const seriesMap = new Map(seriesWithLabel) as any

  const legends = labels?.map(label => label?.split(':')[0])?.filter(Boolean) ?? []
  const sortedLegends = [...legends]?.sort((a, b) => seriesMap.get(b) - seriesMap.get(a))

  const isLegendsEnabled = mergedOptions?.legend?.show ?? true
  const shouldShowCustomLegend = shouldEnableCustomLegends && isLegendsEnabled

  return (
    <div className="PieChart" ref={pieChartContainerRef}>
      <Chart
        className={classNames('pie-chart-component', className)}
        type="donut"
        height={height}
        width={width}
        options={newOptions}
        series={series}
        {...props}
      />
      {shouldShowCustomLegend && <CustomChartLegend legends={sortedLegends} chartId={id} limit={legendLimit} />}
    </div>
  )
}

export default PieChart
